import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import * as d3 from 'd3';


@Component({
  selector: 'app-column-chart-v2',
  templateUrl: './column-chart-v2.component.html',
  styleUrl: './column-chart-v2.component.css',
  standalone: false
})
export class ColumnChartV2Component implements OnInit, OnDestroy {
  @Input() config: any;
  @Input() elementData: any = null;
  @ViewChild('barChartContainer', { static: true }) private barChartContainer!: ElementRef;
  @Output() headingResolved = new EventEmitter<string>();
  @Input() Temp: boolean = false;
  displayName: string = '';
  chartData: ChartRowData[] = [];
  seriesKeys: string[] = [];
  seriesConfig: Record<string, { label: string; color: string }> = {};
  valueFormatByKey: Record<string, string> = {};
  resolvedLabelKey: string = '';
  private apiChartConfig: any = null;
  private readonly seriesPalette: string[] = ['#4a4a4a', '#6b46c1', '#ff8c00', '#0ea5e9', '#16a34a', '#e11d48'];
  readonly maxLabelLength = 18;

  private readonly destroy$ = new Subject<void>();
  private selectedFilters: any = {};
  private inputSearchFilter: string = '';
  private lastSelectedFiltersSignature = '';
  loading: boolean = false;
  noDataFound: boolean = false;

  constructor(
    private reportsService: ReportsService,
    private inbuiltUtilityService: InbuiltUtilityService
  ) {}

  ngOnInit(): void {
    this.selectedFilters = this.config?.selectedFilters || {};
    this.lastSelectedFiltersSignature = JSON.stringify({ filters: this.selectedFilters, search: this.inputSearchFilter });
    this.subscribeToFilterEvents();
    this.callColumnChartDataApi();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) {
          return;
        }

        const nextFilters = event?.selectedFilters || {};
        const nextInputSearchFilter = event?.inputSearchFilter || '';
        const nextSignature = JSON.stringify({ filters: nextFilters, search: nextInputSearchFilter });
        if (nextSignature === this.lastSelectedFiltersSignature) {
          return;
        }

        this.selectedFilters = nextFilters;
        this.inputSearchFilter = nextInputSearchFilter;
        this.lastSelectedFiltersSignature = nextSignature;
        this.callColumnChartDataApi();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.config) {
      return false;
    }

    const eventReport = String(event?.reportNameFromQueryParams ?? event?.report ?? '');
    const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage = String(this.config?.currentPage?.key ?? '');
    const myTab = String(this.config?.currentTab?.key ?? '');

    if (eventReport && eventReport !== myReport) {
      return false;
    }
    if (eventPage && eventPage !== myPage) {
      return false;
    }
    if (eventTab && eventTab !== myTab) {
      return false;
    }

    return true;
  }

  private callColumnChartDataApi(): void {
    this.loading = true;
    let payload = {
      report: this.config?.reportNameFromQueryParams,
      page: this.config?.currentPage ? this.config.currentPage.key : '',
      tab: this.config?.currentTab ? this.config.currentTab.key : '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || 'column_chart',
      download: false,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters),
      inputSearchFilter: this.inputSearchFilter
    };
    if (this.Temp) {
      payload = this.elementData;
    }
    this.reportsService.getInbuildTableData(payload, this.Temp)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          this.apiChartConfig = res?.chartConfig ?? null;
          this.chartData = Array.isArray(res?.data) ? res.data : [];
          this.noDataFound = this.chartData.length === 0;
          this.buildDynamicSeriesConfig(res?.columnInfo, this.chartData);
          this.bindDisplayText(res?.dynamicHeaderTitle || '');
          this.renderChart();
        },
        error: (error: any) => {
          console.error('Bar chart table API error: ', error);
          this.noDataFound = true;
          this.chartData = [];
          this.renderChart();
        }
      });
  }

  get legendItems(): Array<{ label: string; color: string }> {
    return this.seriesKeys.map((key: string) => this.seriesConfig[key]);
  }

  private get chartConfig(): any {
    return this.apiChartConfig ?? this.elementData?.chartConfig ?? {};
  }

  private get labelKey(): string {
    return String(this.chartConfig?.labelKey || '').trim();
  }

  private get colorKey(): string {
    return String(this.chartConfig?.colorkey || this.chartConfig?.colorKey || '').trim();
  }

  get showLegend(): boolean {
    return this.toBooleanFlag(this.chartConfig?.showLegend, true);
  }

  private get showBarScale(): boolean {
    return this.toBooleanFlag(this.chartConfig?.showBar, true);
  }

  private buildDynamicSeriesConfig(columnInfo: any, rows: ChartRowData[]): void {
    this.resolvedLabelKey = this.resolveLabelKey(columnInfo, rows);
    const configuredKeys: string[] = Array.isArray(this.chartConfig?.valueKeyArr)
      ? this.chartConfig.valueKeyArr
          .map((key: any) => String(key ?? '').trim())
          .filter((key: string) => !!key)
      : [];
    const valueKeysFromColumnInfo: string[] = [];
    this.valueFormatByKey = {};

    if (columnInfo && typeof columnInfo === 'object' && !Array.isArray(columnInfo)) {
      Object.keys(columnInfo).forEach((key: string) => {
        const metricType = String(columnInfo[key] ?? '').trim().toUpperCase();
        if (this.resolvedLabelKey && this.norm(key) === this.norm(this.resolvedLabelKey)) {
          return;
        }

        if (metricType === 'SUM' || metricType === 'COUNT') {
          valueKeysFromColumnInfo.push(key);
          this.valueFormatByKey[key] = metricType;
        }
      });
    }

    const fallbackKeys = this.resolveFallbackSeriesKeys(rows);
    const dynamicKeys = configuredKeys.length > 0
      ? configuredKeys
      : (valueKeysFromColumnInfo.length > 0 ? valueKeysFromColumnInfo : fallbackKeys);

    this.seriesKeys = Array.from(new Set<string>(dynamicKeys));
    this.seriesConfig = {};
    this.seriesKeys.forEach((key: string, index: number) => {
      const resolvedMetricType = this.resolveMetricTypeForKey(columnInfo, key);
      if (resolvedMetricType) {
        this.valueFormatByKey[key] = resolvedMetricType;
      }

      this.seriesConfig[key] = {
        label: key,
        color: this.seriesPalette[index % this.seriesPalette.length]
      };
    });
  }

  private resolveFallbackSeriesKeys(rows: ChartRowData[]): string[] {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    const sample = rows[0] || {};
    const segmentNorm = this.norm(this.resolvedLabelKey);
    return Object.keys(sample).filter((key: string) => {
      if (segmentNorm && this.norm(key) === segmentNorm) {
        return false;
      }
      return !Number.isNaN(this.toNumber(sample[key]));
    });
  }

  private norm(value: any): string {
    return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private resolveMetricTypeForKey(columnInfo: any, key: string): string {
    const exactMetricType = String(columnInfo?.[key] ?? '').trim().toUpperCase();
    if (exactMetricType) {
      return exactMetricType;
    }

    const normalizedKey = this.norm(key);
    const matchedKey = Object.keys(columnInfo || {}).find((columnKey: string) => this.norm(columnKey) === normalizedKey);
    return String(matchedKey ? columnInfo?.[matchedKey] ?? '' : '').trim().toUpperCase();
  }

  private resolveLabelKey(columnInfo: any, rows: ChartRowData[]): string {
    if (this.labelKey) {
      return this.labelKey;
    }

    if (columnInfo && typeof columnInfo === 'object' && !Array.isArray(columnInfo)) {
      const dimensionKey = Object.keys(columnInfo).find((key: string) => {
        const metricType = String(columnInfo[key] ?? '').trim().toUpperCase();
        return metricType !== 'SUM' && metricType !== 'COUNT';
      });
      if (dimensionKey) {
        return dimensionKey;
      }
    }

    const sample = rows[0] || {};
    const textKey = Object.keys(sample).find((key: string) => {
      const value = sample[key];
      return typeof value === 'string' && Number.isNaN(Number(value));
    });
    if (textKey) {
      return textKey;
    }

    return Object.keys(sample)[0] || '';
  }

  private getLabelValue(row: ChartRowData): string {
    const configuredLabelKey = this.resolvedLabelKey;
    if (!configuredLabelKey) {
      return '';
    }

    if (row && row[configuredLabelKey] !== undefined && row[configuredLabelKey] !== null && row[configuredLabelKey] !== '') {
      return String(row[configuredLabelKey]);
    }

    const normalizedKey = this.norm(configuredLabelKey);
    const matchedKey = Object.keys(row || {}).find((rowKey: string) => this.norm(rowKey) === normalizedKey);
    if (matchedKey && row[matchedKey] !== undefined && row[matchedKey] !== null && row[matchedKey] !== '') {
      return String(row[matchedKey]);
    }

    return '';
  }

  bindDisplayText(displayText: any): void {
    if (displayText && Object.keys(displayText).length > 0) {
      let key = this.elementData?.name || '';
      if (key.startsWith('{') && key.endsWith('}')) {
        key = key.slice(1, -1).trim();
      }
      const resolved = displayText[key] || '';
      if (resolved) {
        this.displayName = resolved;
        this.headingResolved.emit(resolved);
      }
    }
  }

  truncateLabel(label: string): string {
    if (!label) {
      return '';
    }
    if (label.length <= this.maxLabelLength) {
      return label;
    }
    return `${label.slice(0, this.maxLabelLength)}...`;
  }

  private renderChart(): void {
    const host = this.barChartContainer?.nativeElement;
    if (!host) {
      return;
    }

    d3.select(host).selectAll('*').remove();

    if (!this.chartData.length) {
      return;
    }

    if (!this.seriesKeys.length) {
      return;
    }

    // Ensure tooltip anchors to chart container, not page flow.
    d3.select(host).style('position', 'relative');

    const tooltip = d3
      .select(host)
      .append('div')
      .attr('class', 'bar-tooltip')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('opacity', '0')
      .style('left', '50%')
      .style('top', '8px')
      .style('transform', 'translateX(-50%)')
      .style('background', 'rgba(0, 0, 0, 0.82)')
      .style('color', '#ffffff')
      .style('border-radius', '6px')
      .style('padding', '8px 10px')
      .style('font-size', '11px')
      .style('line-height', '1.3')
      .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.2)')
      .style('max-width', '320px')
      .style('z-index', '9999');

    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;
    const maxLabelLength = this.chartData.reduce(
      (max: number, row: ChartRowData) => Math.max(max, this.getLabelValue(row).length),
      0
    );
    const bottomMargin = Math.max(72, Math.min(120, Math.floor(maxLabelLength * 2.4)));
    const margin = { top: 64, right: 20, bottom: bottomMargin, left: this.showBarScale ? 64 : 20 };
    const hostWidth = host.clientWidth || 760;
    const width = Math.max(320, hostWidth - margin.left - margin.right);
    const outerHeight = Math.max(260, Math.min(460, Math.floor(viewportHeight * 0.52)));
    const height = Math.max(180, outerHeight - margin.top - margin.bottom);

    const categoryCount = Math.max(1, this.chartData.length);
    const x0PaddingInner = categoryCount > 8 ? 0.14 : 0.22;
    const x0PaddingOuter = categoryCount > 8 ? 0.04 : 0.08;
    const x1Padding = categoryCount > 8 ? 0.26 : 0.36;
    const xAxisFontSize = categoryCount > 8 ? 9 : 10;
    const xAxisRotation = categoryCount > 8 ? -48 : -36;

    const svg = d3
      .select(host)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const chart = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    const x0 = d3
      .scaleBand()
      .domain(this.chartData.map((d: ChartRowData) => this.getLabelValue(d)))
      .range([0, width])
      .paddingInner(x0PaddingInner)
      .paddingOuter(x0PaddingOuter);

    const x1 = d3
      .scaleBand()
      .domain(this.seriesKeys)
      .range([0, x0.bandwidth()])
      .padding(x1Padding);

    const allValues = this.chartData.flatMap((d: ChartRowData) =>
      this.seriesKeys.map((key: string) => this.getSeriesValue(d, key))
    );
    const [minValue, maxValue] = this.resolveYAxisDomain(allValues);

    const y = d3
      .scaleLinear()
      .domain([minValue, maxValue])
      .nice()
      .range([height, 0]);

    chart
      .append('line')
      .attr('class', 'zero-line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', y(0))
      .attr('y2', y(0));

    if (this.showBarScale) {
      const yAxis = d3.axisLeft(y).ticks(5).tickSizeOuter(0).tickFormat((value: number) => {
        return this.formatValueByMetric(this.seriesKeys[0] || '', Number(value));
      });

      const yAxisGroup = chart
        .append('g')
        .attr('class', 'y-axis')
        .call(yAxis as any);

      yAxisGroup.select('.domain').attr('stroke', '#c6cfda');
      yAxisGroup
        .selectAll('.tick line')
        .attr('stroke', '#d8e0ea');
      yAxisGroup
        .selectAll('.tick text')
        .attr('font-size', '10px')
        .attr('fill', '#334155');
    }

    const segmentGroups = chart
      .selectAll('.segment-group')
      .data(this.chartData)
      .enter()
      .append('g')
      .attr('class', 'segment-group')
      .attr('transform', (d: ChartRowData) => `translate(${x0(this.getLabelValue(d))},0)`);

    segmentGroups
      .selectAll('rect')
      .data((d: ChartRowData) =>
        this.seriesKeys.map((key: string) => ({
          sourceRow: d,
          labelValue: this.getLabelValue(d),
          key,
          value: this.getSeriesValue(d, key),
          color: this.resolveConfiguredColor(d) || this.seriesConfig[key].color,
          label: this.seriesConfig[key].label
        }))
      )
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', (d: BarDatum) => x1(d.key) ?? 0)
      .attr('y', (d: BarDatum) => (d.value >= 0 ? y(d.value) : y(0)))
      .attr('width', x1.bandwidth())
      .attr('height', (d: BarDatum) => Math.abs(y(d.value) - y(0)))
      .attr('fill', (d: BarDatum) => d.color)
      .on('mousemove', (arg1: any, arg2: any, arg3: any) => {
  const datum = this.extractBarDatum(arg1, arg2);
  if (!datum) {
    tooltip.style('opacity', '0');
    return;
  }
  tooltip
    .style('opacity', '1')
    .style('left', '50%')
    .style('top', '8px')
    .style('transform', 'translateX(-50%)')
    .html(this.getBarTooltipHtml(datum));
})
      .on('mouseleave', () => {
        tooltip.style('opacity', '0');
      });

    segmentGroups
      .selectAll('.bar-value-label')
      .data((d: ChartRowData) =>
        this.seriesKeys.map((key: string) => ({
          key,
          value: this.getSeriesValue(d, key),
          displayValue: this.formatValueByMetric(key, this.getSeriesValue(d, key))
        }))
      )
      .enter()
      .append('text')
      .attr('class', 'bar-value-label')
      .attr('font-size', '0.75rem')
      .attr('fill', '#1e293b')
      .attr('text-anchor', 'start')
      .attr('transform', (d: BarLabelDatum) => {
        const cx = (x1(d.key) ?? 0) + x1.bandwidth() / 2;
        // Always place label above the bar — no clamping so tall bars never get label inside them
        const cy = d.value >= 0 ? y(d.value) - 4 : y(d.value) + 18;
        return `translate(${cx},${cy}) rotate(-90)`;
      })
      .text((d: BarLabelDatum) => d.displayValue);

    const xAxis = d3.axisBottom(x0).tickFormat((segment: string) => this.truncateLabel(String(segment ?? '')));

    const xAxisGroup = chart
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis);

    xAxisGroup.select('.domain').attr('stroke', '#c6cfda');

    xAxisGroup
      .selectAll('.tick text')
      .attr('font-size', `${xAxisFontSize}px`)
      .attr('transform', `rotate(${xAxisRotation})`)
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.4em');

    xAxisGroup
      .selectAll('.tick text')
      .append('title')
        .text((segment: string) => String(segment ?? ''));
  }

  private formatCompactCurrency(value: number): string {
    const absoluteValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absoluteValue >= 1_000_000_000) {
      return `${sign}$${(absoluteValue / 1_000_000_000).toFixed(2)}B`;
    }
    if (absoluteValue >= 1_000_000) {
      return `${sign}$${(absoluteValue / 1_000_000).toFixed(2)}M`;
    }
    if (absoluteValue >= 1_000) {
      return `${sign}$${(absoluteValue / 1_000).toFixed(2)}K`;
    }
    return `${sign}$${absoluteValue.toFixed(2)}`;
  }

  private formatCount(value: number): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
      minimumFractionDigits: 0
    }).format(value);
  }

  private formatValueByMetric(key: string, value: number): string {
    const metricType = String(this.valueFormatByKey[key] ?? '').toUpperCase();
    if (metricType === 'SUM') {
      return this.formatCompactCurrency(value);
    }
    return this.formatCount(value);
  }

  private resolveYAxisDomain(values: number[]): [number, number] {
    const rawMaxValue = Math.max(0, d3.max(values) ?? 0);
    if (rawMaxValue <= 0) {
      return [0, 1];
    }

    const magnitude = Math.pow(10, Math.floor(Math.log10(rawMaxValue)));
    const step = Math.max(1, magnitude / 2);
    const roundedMax = Math.ceil(rawMaxValue / step) * step;

    return [0, roundedMax];
  }

  private toBooleanFlag(value: any, fallback: boolean): boolean {
    if (value === undefined || value === null || value === '') {
      return fallback;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }

    return fallback;
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const normalized = String(value)
      .trim()
      .replace(/^\((.*)\)$/, '-$1')
      .replace(/[$,%\s]/g, '')
      .replace(/,/g, '');

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getSeriesValue(row: ChartRowData, key: string): number {
    const matchedKey = this.resolveRowKey(row, key);
    if (matchedKey && row[matchedKey] !== undefined && row[matchedKey] !== null && row[matchedKey] !== '') {
      return this.toNumber(row[matchedKey]);
    }

    return 0;
  }

  private resolveRowKey(row: ChartRowData | undefined, key: string): string | undefined {
    if (!row || !key) {
      return undefined;
    }

    const keys = Object.keys(row || {});
    if (keys.includes(key)) {
      return key;
    }

    const normalizedKey = this.norm(key);
    const normalizedKeyNoTotal = normalizedKey.replace(/total$/i, '');

    const exactMatch = keys.find((rowKey: string) => this.norm(rowKey) === normalizedKey);
    if (exactMatch) {
      return exactMatch;
    }

    const normalizedCandidates = keys.map((rowKey: string) => ({
      rowKey,
      normalized: this.norm(rowKey),
      normalizedNoTotal: this.norm(rowKey).replace(/total$/i, '')
    }));

    const partialMatch = normalizedCandidates.find((candidate) => {
      return (
        candidate.normalized.includes(normalizedKeyNoTotal) ||
        normalizedKeyNoTotal.includes(candidate.normalized) ||
        candidate.normalizedNoTotal.includes(normalizedKeyNoTotal) ||
        normalizedKeyNoTotal.includes(candidate.normalizedNoTotal)
      );
    });

    return partialMatch?.rowKey;
  }

  private resolveConfiguredColor(row: ChartRowData): string | undefined {
    if (!this.colorKey) {
      return undefined;
    }

    const parentColorConfig = this.elementData?.parentConfig?.color;
    if (!parentColorConfig || typeof parentColorConfig !== 'object' || Array.isArray(parentColorConfig)) {
      return undefined;
    }

    const colorBucketKey = this.resolveObjectKey(parentColorConfig, this.colorKey);
    if (!colorBucketKey) {
      return undefined;
    }

    const colorBucket = parentColorConfig[colorBucketKey];
    if (!colorBucket || typeof colorBucket !== 'object' || Array.isArray(colorBucket)) {
      return undefined;
    }

    const rowColorSourceKey = this.resolveRowKey(row, this.colorKey);
    const rowColorSourceValue = rowColorSourceKey ? row[rowColorSourceKey] : undefined;
    const normalizedColorSourceValue = String(rowColorSourceValue ?? '').trim();
    if (!normalizedColorSourceValue) {
      return undefined;
    }

    const mappedValueKey = this.resolveObjectKey(colorBucket, normalizedColorSourceValue);
    if (!mappedValueKey) {
      return undefined;
    }

    const mappedColor = String(colorBucket[mappedValueKey] ?? '').trim();
    return mappedColor || undefined;
  }

  private resolveObjectKey(source: Record<string, any>, targetKey: string): string | undefined {
    if (!source || !targetKey) {
      return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(source, targetKey)) {
      return targetKey;
    }

    const normalizedTarget = this.norm(targetKey);
    return Object.keys(source).find((key: string) => this.norm(key) === normalizedTarget);
  }

  private getBarTooltipHtml(d: BarDatum): string {
    const segmentName = String(this.getLabelValue(d.sourceRow || ({} as ChartRowData)) || d.labelValue || '').trim() || this.resolvedLabelKey || 'Label';
    const keyName = String(d.label ?? d.key ?? '').trim() || 'Cost';
    const rawValue = this.getSeriesValue(d.sourceRow || ({} as ChartRowData), d.key);
    const formattedValue = this.formatValueByMetric(d.key, rawValue);

    return `
      <div class="tooltip-header">${segmentName}</div>
      <div class="tooltip-content">
        <div class="tooltip-row"><span class="tooltip-key">${keyName}:</span> <span class="tooltip-value">${formattedValue}</span></div>
      </div>
    `;
  }

  private extractBarDatum(arg1: any, arg2: any): BarDatum | null {
    if (this.isBarDatum(arg1)) {
      return arg1;
    }

    if (this.isBarDatum(arg2)) {
      return arg2;
    }

    return null;
  }

  private isBarDatum(value: any): value is BarDatum {
    return !!value && typeof value === 'object' && typeof value.key === 'string' && value.sourceRow;
  }

  private extractMouseEvent(arg1: any): MouseEvent | null {
    if (arg1 && typeof arg1 === 'object' && 'clientX' in arg1 && 'clientY' in arg1) {
      return arg1 as MouseEvent;
    }

    const d3Event = (d3 as any).event;
    if (d3Event && typeof d3Event === 'object' && 'clientX' in d3Event && 'clientY' in d3Event) {
      return d3Event as MouseEvent;
    }

    return null;
  }
}

interface BarDatum {
  sourceRow: ChartRowData;
  labelValue: string;
  key: string;
  value: number;
  color: string;
  label: string;
}

interface ChartRowData {
  [key: string]: any;
}

interface BarLabelDatum {
  key: string;
  value: number;
  displayValue: string;
}
